import useFetchQrCode from '@shared/hooks/useFetchQrCode';
import { QrCode } from '@shared/ui/qr-code';
import { PreloaderCircular } from '@shared/ui/kit';
import * as stylex from '@stylexjs/stylex';
import { FC } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { styles } from './styles';
import { SminexEventIcon } from '@shared/assets/icons';
import { palette } from '@shared/tokens/palette.stylex';
import WaitingClockImg from './assets/waiting-clock.webp';
import EventFinishedImg from './assets/event-finished.webp';
import { formatEventDate } from '@shared/libs/format-event-date';
import { DEFAULT_COLOR, mapColorToPalette } from '@entities/category';
import { LeadCollectorTicket } from '@widgets/leadCollector';

export const QrPage: FC = () => {
  const { id } = useParams();
  const { data, fetching, error } = useFetchQrCode(id as string, 320);

  if (fetching)
    return (
      <div {...stylex.props(styles.preloaderContainer)}>
        <PreloaderCircular variant="secondary" />
      </div>
    );

  if (error) {
    if (error.graphQLErrors && Array.isArray(error.graphQLErrors)) {
      const errorMessage = error.graphQLErrors[0]?.message;

      if (errorMessage === 'User not found for ticket') {
        return <Navigate to="/error-for-remote" />;
      }
    }

    return <Navigate to="/uncaught-error" />;
  }

  const renderContent = () => {
    if (!data?.getTicketQrcode) return null;

    const { status, qr, categories, eventName, dateStart, dateEnd, isDefaultEvent } = data.getTicketQrcode;

    const formattedDate = formatEventDate(dateStart, dateEnd);

    switch (status) {
      case 'pending':
        return (
          <div {...stylex.props(styles.content)}>
            <img
              src={WaitingClockImg}
              alt="Иллюстрация: мероприятие еще не началось"
              {...stylex.props(styles.waitingClockIllustration)}
            />
            <h2 {...stylex.props(styles.titleInfo)}>«{eventName}»</h2>
            <p {...stylex.props(styles.date)}>{formattedDate}</p>
            <h3 {...stylex.props(styles.subTitleInfo)}>QR-код будет доступен в день начала события</h3>
          </div>
        );

      case 'completed':
        return (
          <div {...stylex.props(styles.content)}>
            <img
              src={EventFinishedImg}
              alt="Иллюстрация: мероприятие завершилось"
              {...stylex.props(styles.waitingClockIllustration)}
            />
            <h2 {...stylex.props(styles.titleInfo)}>«{eventName}»</h2>
            <p {...stylex.props(styles.date)}>{formattedDate}</p>
            <h3 {...stylex.props(styles.subTitleInfo)}>Мероприятие завершилось</h3>
          </div>
        );

      case 'active':
        return isDefaultEvent ? (
          <div {...stylex.props(styles.content)}>
            <QrCode svg={qr} />

            <ul {...stylex.props(styles.categoriesWrapper)}>
              {categories.map(({ title, color }, i) => (
                <li
                  key={i}
                  {...stylex.props(
                    styles.categoryItem,
                    !!color && color !== DEFAULT_COLOR && styles.categoryItemColored(mapColorToPalette[color])
                  )}
                >
                  {title}
                </li>
              ))}
            </ul>

            <h3 {...stylex.props(styles.title)}>«{eventName}»</h3>
            <p {...stylex.props(styles.subTitle)}>Предъявите QR для прохода на мероприятие</p>
          </div>
        ) : (
          <LeadCollectorTicket ticketInfo={data.getTicketQrcode} qr={qr} />
        );

      case 'blocked':
        return (
          <div {...stylex.props(styles.content)}>
            <img
              src={EventFinishedImg}
              alt="Иллюстрация: мероприятие завершилось"
              {...stylex.props(styles.waitingClockIllustration)}
            />
            <h3 {...stylex.props(styles.subTitleInfoBlocked)}>Билет недействителен</h3>
            <p {...stylex.props(styles.infoOrg)}>Билет был отозван организатором мероприятия</p>
          </div>
        );

      default:
        return (
          <div {...stylex.props(styles.content)}>
            <p {...stylex.props(styles.title)}>«{eventName}»</p>
            <div {...stylex.props(styles.subTitle)}>Неизвестный статус: {status}</div>
          </div>
        );
    }
  };

  const shouldShowIcon = data?.getTicketQrcode?.status !== 'blocked';

  return (
    <div translate="no" {...stylex.props(styles.container)}>
      {shouldShowIcon && <SminexEventIcon colorPrimary={palette['grey-950']} colorSecondary={palette['bronze-500']} />}
      {renderContent()}
    </div>
  );
};
